import {
  BackgroundTheme,
  BackgroundThemeRevision,
  PageTheme,
  PageThemeRevision,
  Group,
  GroupReferral,
  GroupTheme,
  GroupThemeRevision,
  HeaderTheme,
  HeaderThemeRevision,
  NavbarTheme,
  NavbarThemeRevision,
  ReferralTheme,
  ReferralThemeRevision,
  Page,
} from 'referral-types';
import { getOpacityStyle, MediaPosition, MediaRepeat, MediaSize, OpacityLevel, Size } from '../../constants';
import { CSSProperties, ReactNode } from 'react';
import { type FormFields as GroupReferralFormFields } from '../group-referral/group.schema';
import { HeaderActionProps } from '../_misc/layouts/constants';
import { ButtonType } from '../_misc/buttons/ButtonType';
import { RgbSubThemeOptions } from './groups/constants';
import { Color } from '../../context/theme/ThemeConstants';
import { UseFormReturn } from 'react-hook-form';
import type { FormFields as GroupFormFields } from '../group/group.schema';
import { HippieWavesSubThemeOptions } from './backgrounds/hippie-waves/HippieWavesBackground';
import { FrostedGlassSubThemeOptions } from './backgrounds/frosted/FrostedBackground';
import { RainbowGradientSubThemeOptions } from './backgrounds/rainbow/backgrounds/AnimatedRainbowGradient';
import { StripeSubThemeOptions } from './backgrounds/stripes/StripesBackground';
import { GeometricSubThemeOptions } from './backgrounds/geometric/GeometricBackgrounds';
import { GeometricGradientSubThemeOptions } from './backgrounds/geometric-gradient/GeometricGradientBackgrounds';
import { GridSubThemeOptions } from './backgrounds/grid/GridBackgrounds';
import { RippleSubThemeOptions } from './backgrounds/ripples/RippleBackgrounds';
import { WaveSubThemeOptions } from './backgrounds/waves/WaveBackgrounds';
import { SimpleGradientSubThemeOptions } from './backgrounds/simple-gradient/SimpleGradientBackground';

export type SubThemeBaseProps = {
  subThemeOptions?: Array<SubThemeOption>;
  currentSubTheme?: SubThemeOption;
};

export type BackgroundThemeWrapperProps = {
  mediaOverride?: File | null;
  mediaMobileOverride?: File | null;
  themeRevision?: Partial<BackgroundThemeRevision>;
  navbarSize?: Size;
  topOffset?: number;
  pageId?: string;
  isEditing?: boolean;
  isPreviewing?: boolean;
};

export type GroupThemeWrapperProps = {
  group: Partial<Group>;
  themeRevision?: Partial<GroupThemeRevision>;
  mediaOverride?: File | null;
  pageId?: string;

  anchorId: string;
  isEditing?: boolean;
  isPreviewing?: boolean;
  editingActions?: HeaderActionProps;
  isFirst?: boolean;

  onSubmit?: (formValues: GroupFormFields) => Promise<void>;

  Body: ReactNode;
  Footer?: ReactNode;
};

export type GroupThemeProps = SubThemeBaseProps &
  GroupThemeWrapperProps & {
    form?: UseFormReturn<GroupFormFields>;
  };

export type ReferralThemeWrapperProps = {
  referralId: string;
  groupReferral: Partial<GroupReferral>;
  themeRevision?: ReferralThemeRevision;

  anchorId: string;

  isEditing?: boolean;
  isPreviewing?: boolean;

  editingActions?: HeaderActionProps;
  onSubmit?: (formValues: GroupReferralFormFields) => Promise<void>;

  handleCardClick?: () => void;

  page?: Page;

  Footer?: ReactNode; // pass in Node so that we can use usePurchase/usePage/etc contexts
  Media?: ReactNode;
};

export type ReferralThemeProps = SubThemeBaseProps &
  ReferralThemeWrapperProps & {
    form: UseFormReturn<GroupReferralFormFields>;
    cssProperties?: CSSProperties;
  };

export type HeaderThemeWrapperProps = {
  themeRevision?: Partial<HeaderThemeRevision>;
  mediaOverride?: File | null;
  pageSize: Size;
  pageId?: string;
  userId: string;
  isEditing?: boolean;
  isPageEditing?: boolean;
  canEdit?: boolean;
  toggleEditing?: (isEditing?: boolean) => void;
};

export type HeaderThemeProps = SubThemeBaseProps & HeaderThemeWrapperProps;

export type NavbarThemeWrapperProps = {
  themeRevision?: Partial<NavbarThemeRevision>;
  pageThemeRevision?: Partial<PageThemeRevision>;
  removeBorder?: boolean;
  renderCartToggle?: (buttonType: ButtonType) => ReactNode;
  isLightingLocked?: boolean;
  needsLowerZ?: boolean;
  logoOverride?: File | null;
  pageId?: string;
  isEditing?: boolean;
  isPreviewing?: boolean;
  isNavbarButtonsDisabled?: boolean;
  hasSubNav: boolean;
  removeShadow?: boolean;
  forceBorder?: boolean;
};

export type NavbarThemeProps = NavbarThemeWrapperProps & {
  backgroundClassOverride?: string;
  isVibrant?: boolean;
  isInverted?: boolean;
  buttonType: ButtonType;
  isLightingLocked?: boolean;
};

export type NavbarButtonProps = {
  revision?: Partial<NavbarThemeRevision>;
  buttonType: ButtonType;
};

// THEME IDS

export enum BACKGROUND_THEME_IDS {
  default = '34462f0a-f419-4120-8cd6-b9542925f8e7',
  basic = '7dfe0d58-4bfb-4d3a-b225-b99f5485f5c5',
  solidColor = '099cfd37-53fe-4881-9ece-d19b97e016c8',
  frostedGlass = 'b17b3e62-0d87-47d5-8b34-9c16e2179a01',
  hippieWaves = '9a328705-3489-4e39-8650-e2a85e9905c2',
  triColorGoemetricPatterns = '84ede986-e0a9-4a61-b42c-5a922424c181',
  mulligansOdyssey = '06d8cbd7-783e-47fc-aa08-5219a79e8bc0',
  rainbowGradient = '242487a7-db14-45ef-a6be-9f274c85fdcb',
  hearts = 'e1c7b2a3-4d8f-4e2a-9b1c-7f3d2e6a9c1b',
  digitalRain = '3f8b2d47-1a9c-4e6f-b5d8-7c4a9e2f6b83',
  rainbowRain = '762b3ae1-3d17-460b-ba3c-378896d4fb6a',
  overlappingCirclesLite = '91f64b3a-7af9-4b28-87a1-9b14f2d6b8f0',
  overlappingCirclesPro = '8f1d4c6b-9a5e-4b7f-a2c3-5d7e8b1a9f4c',
  overlappingCirclesUltra = '2c7b8e5d-3f1a-4d9f-8b4c-7a6f2d3e1b8c',
  interlockingStripesLite = '0d6f6cf9-b91d-42f4-9109-91b9651c1dbf',
  interlockingStripesPro = '5a9c3d8e-1b6f-4c2a-85d7-9e4f1b7c2a6d',
  interlockingStripesUltra = '7d4e9a6f-2c5b-4f8a-1d3c-6b9e2f8c5a1d',
  overlappingStripesLite = 'a9e579d6-3c5d-42db-9d6b-7682d594f23c',
  overlappingStripesPro = 'f3b7a5e2-9d6c-4f1b-87a6-5e2c3d8f4b9a',
  overlappingStripesUltra = 'a8c5d3f9-2e7b-4c1f-6a3d-9b4e7f5a2c8d',
  overlappingCubesLite = '9f2a7d5e-4b1c-8a6f-3d9e-5c7b1a8f2d4e',
  overlappingCubesPro = 'b8d5e3f7-2a6c-4f9a-1b3d-7e4f2c8a5d1b',
  overlappingCubesUltra = 'c3d9f7a5-1b4e-6a2f-8c5d-7e1b9a3f4d8c',
  interlockingCubesLite = 'd9f3a7b1-5e4c-6f8a-2d3b-7c1e5f9a4b6d',
  interlockingCubesPro = 'b2c5e8f4-1d7a-6b9f-3c2e-5a4d7f1b8c3a',
  interlockingCubesUltra = 'a6b7d2e9-4f1c-5a3f-8b9e-7c2d1f6a4e5b',
  cubes3dLite = 'e1b9a5f3-6d4c-2f7a-5b8e-3c9d4a7f1b6e',
  cubes3dPro = 'c8a4f7d3-5b1e-6f9a-2c7d-4e3b9f5a1d6c',
  cubes3dUltra = 'f3d1a8c7-4b5e-6a2f-9d3c-7e1b8a4f5d9e',
  customMedia = 'a1b2c3d4-5e6f-7a8b-9c0d-1e2f3a4b5c6d',
  particles = 'e0e3abce-6356-462a-a580-5a72161328ce',
  stripes = '4ae7dd67-22c5-4a5f-b94d-46719aaa58e1',
  ripples = 'f47ac10b-58cc-4372-a567-0e02b2c3d479',
  apartmentFloor = '9b1deb4d-3b7d-4bad-9bdd-2b0d7b3dcb6d',
  radialSunburst = '3d5d0db0-e9b4-4e9c-a4d1-6e8f2d1c7f3a',
  waves = 'a0eebc99-9c0b-4ef8-bb6d-6bb9bd380a11',
  grids = 'e4c2f5d1-6b7a-4f3e-9d8c-2a1f5e7b9d0c',
  geometric = '6ba7b810-9dad-11d1-80b4-00c04fd430c8',
  presetVideoTest = '1f8c9a76-3d12-4a68-9f07-2b6f5c1d8e44',
  seasonalPresetImageTest = 'b3d4e2f1-7c9a-4f5b-8d21-6a0e3c9f7b52',
  simpleGradient = '2c5a8d1f-4e7b-43f9-a6d2-8c1b9e5f7a3d',
  nebulaSmoke = '7e4c2d1f-8a6b-4f3e-9d5c-1b7e3f8a2c5d',
  cosmicGlass = '5d3f1c7e-9b4a-4e6f-8c2d-7a1e5b3f9d8c',
}

export enum GROUP_THEME_IDS {
  default = '79d914cf-a814-4c44-8ea4-6423ed605e36',
  basic = 'bdd3fc59-90d0-4e91-892f-90f30d4ad8c2',
  basicPro = '665f90a0-e572-49c6-9cf4-82c02566325b',
  basicUltra = 'ad8ad9b4-6b2a-44d4-8322-6aeb82850f3b',
  rgbLite = '4cf5bc42-7a85-4ff7-bf76-b35c8e66cd1d',
  rgbUltra = 'e7d2c9a5-3b6f-49d3-839f-182a74b0a1c4',
  orbiter = 'fa9adfc1-485d-41b6-9d0c-68b5c18f9c60',
  browserPro = 'c8a4e2f1-7d3b-4a5c-9f6e-2b1d8c4a7e3f',
}

export enum REFERRAL_THEME_IDS {
  default = 'b0fa1a76-d072-40f7-a62e-9f2120f5c4b3',
  basic = 'addf2174-2c3b-4332-9ece-620d64abbfc0',
  basicPro = 'a036adf7-8f91-4b99-bdf9-a5602072d767',
  basicUltra = 'ac61d490-11c0-4fae-81d9-8efe2b6e17af',
  simple = 'de2b6f11-4a80-4ab6-8f2a-5f2c1d9e7a31',
  simplePro = 'f3a1c8d4-2b7e-4e51-9a6d-1c8b4f2e7d90',

  expandable = '4f84ec3b-d8d2-48e6-9390-7e378b91d289',
  expandablePro = 'be99f812-c470-40c7-a94e-33c8be7aace1',
  expandableUltra = '98f3d869-ff32-472d-883e-e579c28e2d2a',

  cardless = 'e1a9b7c3-5d4f-4e82-9f6a-3c8d2b7e1a5f',
  cardlessPro = 'a7f3d2e8-9c1b-4a5f-8e6d-4b2c7f1a9e3d',

  showcase = '4dce5fdd-ddcb-4746-9a21-7e6dff8e314e',
  showcasePro = 'b00960c3-f0f3-4440-9e77-df11b8eafe7b',
}

export enum NAVBAR_THEME_IDS {
  default = '151ac1ba-23c0-448d-9d02-61bfd0ff42d6',
  basicPro = '8eca0b40-42ce-4d74-929a-170e344e435e',
  basicUltra = 'e81b2e5b-9648-4be7-9e63-91cfc9389ab6',

  vibrantLite = 'a10db4c6-fb26-43fd-8fea-aa1ea26a17d8',
  vibrantUltra = '31bfa261-3d4d-45e8-bb71-60c8e6d3c607',
}

export enum HEADER_THEME_IDS {
  default = 'cbae0547-143b-4f8f-a5e8-94313829fcac',
  basic = '058cb3db-e588-4141-821b-e1a6d905a1fb',
  basicPro = 'e0405ff7-f1ba-4bc0-b1d1-e0b457afff46',
  basicUltra = '5eacb2d1-5b26-48bc-842e-1a1bff7592bc',
  rgbLite = '3d7a25f1-4e69-41ab-b7f3-8c29d7b0d8e2',
  rgbUltra = '9f86c3e8-71b5-4a2c-8e3d-bf28a47e6c90',
  hero = 'c5f9e4d3-af6b-4e8f-9c3d-7b2a5f4c8e6d',
  noHeader = 'a1c4e7f2-3b8d-4e6a-9f1c-2d5b8e3a7c40',
}

// GET SUBTHEME OPTIONS FUNCTIONS

export function getBackgroundSubThemeOptions(themeId?: string): Array<SubThemeOption> | undefined {
  switch (themeId) {
    case BACKGROUND_THEME_IDS.interlockingCubesLite:
    case BACKGROUND_THEME_IDS.interlockingStripesLite:
    case BACKGROUND_THEME_IDS.overlappingCubesLite:
    case BACKGROUND_THEME_IDS.overlappingCirclesLite:
      return LiteColorSchemeOptions;
    case BACKGROUND_THEME_IDS.interlockingCubesUltra:
    case BACKGROUND_THEME_IDS.interlockingStripesUltra:
    case BACKGROUND_THEME_IDS.overlappingCubesUltra:
    case BACKGROUND_THEME_IDS.overlappingCirclesUltra:
    case BACKGROUND_THEME_IDS.digitalRain:
      return ColorSchemeOptions.filter((o) => o.value !== ColorScheme.inverted);
    case BACKGROUND_THEME_IDS.frostedGlass:
      return FrostedGlassSubThemeOptions;
    case BACKGROUND_THEME_IDS.rainbowGradient:
      return RainbowGradientSubThemeOptions;
    case BACKGROUND_THEME_IDS.hippieWaves:
      return HippieWavesSubThemeOptions;
    case BACKGROUND_THEME_IDS.stripes:
      return StripeSubThemeOptions;
    case BACKGROUND_THEME_IDS.particles:
      return ColorSchemeOptions;
    case BACKGROUND_THEME_IDS.geometric:
      return GeometricSubThemeOptions;
    case BACKGROUND_THEME_IDS.ripples:
      return RippleSubThemeOptions;
    case BACKGROUND_THEME_IDS.seasonalPresetImageTest:
      return SeasonalSubThemeOptions;
    case BACKGROUND_THEME_IDS.simpleGradient:
      return SimpleGradientSubThemeOptions;
    case BACKGROUND_THEME_IDS.nebulaSmoke:
      return NebulaSmokeSubThemeOptions;
    case BACKGROUND_THEME_IDS.cosmicGlass:
      return CosmicGlassSubThemeOptions;
    case BACKGROUND_THEME_IDS.grids:
      return GridSubThemeOptions;
    case BACKGROUND_THEME_IDS.waves:
    case BACKGROUND_THEME_IDS.solidColor:
    case BACKGROUND_THEME_IDS.radialSunburst:
    case BACKGROUND_THEME_IDS.apartmentFloor:
    default:
      return;
  }
}

export function getGroupSubThemeOptions(themeId?: string): Array<SubThemeOption> | undefined {
  switch (themeId) {
    case GROUP_THEME_IDS.basicUltra:
    case GROUP_THEME_IDS.rgbLite:
    case GROUP_THEME_IDS.rgbUltra:
    case GROUP_THEME_IDS.browserPro:
      return RgbSubThemeOptions;
    case GROUP_THEME_IDS.basic:
    case GROUP_THEME_IDS.basicPro:
    case GROUP_THEME_IDS.orbiter:
    default:
      return;
  }
}

export function getReferralSubThemeOptions(themeId?: string): Array<SubThemeOption> | undefined {
  switch (themeId) {
    case REFERRAL_THEME_IDS.basicPro:
    case REFERRAL_THEME_IDS.simplePro:
      return RgbSubThemeOptions;
    case REFERRAL_THEME_IDS.simple:
    case REFERRAL_THEME_IDS.expandablePro:
    case REFERRAL_THEME_IDS.basicUltra:
    case REFERRAL_THEME_IDS.expandableUltra:
    case REFERRAL_THEME_IDS.cardless:
    case REFERRAL_THEME_IDS.cardlessPro:
    default:
      return;
  }
}

export function getHeaderSubThemeOptions(themeId?: string): Array<SubThemeOption> | undefined {
  switch (themeId) {
    case HEADER_THEME_IDS.basicUltra:
    case HEADER_THEME_IDS.rgbLite:
    case HEADER_THEME_IDS.rgbUltra:
      return RgbSubThemeOptions;
    case HEADER_THEME_IDS.hero:
      return undefined;
    default:
      return;
  }
}

export function getNavbarSubThemeOptions(themeId?: string): Array<SubThemeOption> | undefined {
  switch (themeId) {
    case NAVBAR_THEME_IDS.basicPro:
    case NAVBAR_THEME_IDS.vibrantLite:
    case NAVBAR_THEME_IDS.basicUltra:
    case NAVBAR_THEME_IDS.vibrantUltra:
    default:
      return;
  }
}

// SUBTHEME ENUMS AND CONSTS

export enum ColorScheme {
  default = 'default',
  inverted = 'inverted',
  primary = 'primary',
  secondary = 'secondary',
  tertiary = 'tertiary',
  // default = "default",
  // inverted = "inverted",
  // primary = "primary",
  // primaryIntense = "primaryIntense",
  // primaryMuted = "primaryMuted",
  // secondary = "secondary",
  // secondaryIntense = "secondaryIntense",
  // secondaryMuted = "secondaryMuted",
  // tertiary = "tertiary",
  // tertiaryIntense = "tertiaryIntense",
  // tertiaryMuted = "tertiaryMuted",
}

export const ColorSchemeOptions: Array<SubThemeOption> = [
  { value: ColorScheme.default, label: 'Default' },
  { value: ColorScheme.inverted, label: 'Inverted', isInverted: true },
  { value: ColorScheme.primary, label: 'Primary', isVibrant: true },
  // { value: ColorScheme.primaryIntense, label: 'Primary Intense', isVibrant: true },
  // { value: ColorScheme.primaryMuted, label: 'Primary Muted', isVibrant: true },
  { value: ColorScheme.secondary, label: 'Secondary', isVibrant: true },
  // { value: ColorScheme.secondaryIntense, label: 'Secondary Intense', isVibrant: true },
  // { value: ColorScheme.secondaryMuted, label: 'Secondary Muted', isVibrant: true },
  { value: ColorScheme.tertiary, label: 'Tertiary', isVibrant: true },
  // { value: ColorScheme.tertiaryIntense, label: 'Tertiary Intense', isVibrant: true },
  // { value: ColorScheme.tertiaryMuted, label: 'Tertiary Muted', isVibrant: true },
];

export const LiteColorSchemeOptions = ColorSchemeOptions.filter((_, i) => i < 3);

export const SeasonalSubThemeOptions: Array<SubThemeOption> = [
  { value: 'winter', label: 'Winter' },
  { value: 'summer', label: 'Summer' },
  { value: 'spring', label: 'Spring' },
  { value: 'fall', label: 'Fall' },
];

export const NebulaSmokeSubThemeOptions: Array<SubThemeOption> = [
  { value: 'nebula-silky-noise', label: 'Silky Noise' },
  { value: 'nebula-smoky-grain', label: 'Smoky Grain' },
  { value: 'nebula-ocean-mist', label: 'Ocean Mist' },
  { value: 'nebula-violet-haze', label: 'Violet Haze' },
  { value: 'nebula-cloudy-grain', label: 'Cloudy Grain' },
  { value: 'nebula-velvet-noise', label: 'Velvet Noise' },
  { value: 'nebula-silky-grain', label: 'Silky Grain' },
  { value: 'nebula-warm-grainy', label: 'Warm Grainy' },
  { value: 'nebula-cool-grain', label: 'Cool Grain' },
  { value: 'nebula-warm-blur', label: 'Warm Blur' },
  { value: 'nebula-faded-blur', label: 'Faded Blur' },
  { value: 'nebula-velvet-grain', label: 'Velvet Grain' },
  { value: 'nebula-sandstone-blur', label: 'Sandstone Blur' },
  { value: 'nebula-slate-blur', label: 'Slate Blur' },
];

export const CosmicGlassSubThemeOptions: Array<SubThemeOption> = [
  { value: 'cosmic-milky-glass', label: 'Milky Glass' },
  { value: 'cosmic-frosted-light', label: 'Frosted Light' },
  { value: 'cosmic-clear-resin', label: 'Clear Resin' },
  { value: 'cosmic-intersecting-pain', label: 'Intersecting Pain' },
  { value: 'cosmic-rainbow-glass', label: 'Rainbow Glass' },
  { value: 'cosmic-nebulous-glass', label: 'Nebulous Glass' },
  { value: 'cosmic-spectrum-overlay', label: 'Spectrum Overlay' },
];

export type SubThemeOption = {
  value: string;
  label: string;
  colorOverride?: Color;
  isVibrant?: boolean;
  isInverted?: boolean;
};

export function getColorHexs({
  colorScheme,
  isDarkMode,
  primaryColorName,
}: {
  colorScheme?: ColorScheme | string;
  isDarkMode?: boolean;
  primaryColorName: string;
}): ColorHexs {
  const getAdjustedHexs = (hexs: {
    ultraDark: string;
    extraDark: string;
    dark: string;
    base: string;
    light: string;
    extraLight: string;
    ultraLight: string;
  }) => ({
    extraIntense: isDarkMode ? hexs.extraLight : hexs.extraDark,
    intense: isDarkMode ? hexs.light : hexs.dark,
    base: hexs.base,
    muted: isDarkMode ? hexs.dark : hexs.light,
    extraMuted: isDarkMode ? hexs.extraDark : hexs.extraLight,
  });

  const scheme = colorScheme === ColorScheme.primary ? primaryColorName : colorScheme;

  switch (scheme) {
    case ColorScheme.inverted:
      return getAdjustedHexs({
        ultraDark: '#F9FAFB', // 50
        extraDark: '#F3F4F6', // 100
        dark: '#D1D5DB', // 300
        base: '#6B7280', // 500
        light: '#374151', // 700
        extraLight: '#111827', // 900
        ultraLight: '#030712', // 950
      });
    // case ColorScheme.red:
    //     return getAdjustedHexs({
    //         ultraDark: '#450A0A', // 950
    //         extraDark: '#7F1D1D', // 900
    //         dark: '#B91C1C', // 700
    //         base: '#EF4444', // 500
    //         light: '#FCA5A5', // 300
    //         extraLight: '#FEE2E2', // 100
    //         ultraLight: '#FEF2F2', // 50
    //     });
    // case ColorScheme.orange:
    //     return getAdjustedHexs({
    //         ultraDark: '#431407', // 950
    //         extraDark: '#7C2D12', // 900
    //         dark: '#C2410C', // 700
    //         base: '#F97316', // 500
    //         light: '#FDBA74', // 300
    //         extraLight: '#FFEDD5', // 100
    //         ultraLight: '#FFF7ED', // 50
    //     });
    // case ColorScheme.amber:
    //     return getAdjustedHexs({
    //         ultraDark: '#451A03', // 950
    //         extraDark: '#78350F', // 900
    //         dark: '#B45309', // 700
    //         base: '#F59E0B', // 500
    //         light: '#FCD34D', // 300
    //         extraLight: '#FEF3C7', // 100
    //         ultraLight: '#FFFBEB', // 50
    //     });
    // case ColorScheme.yellow:
    //     return getAdjustedHexs({
    //         ultraDark: '#422006', // 950
    //         extraDark: '#713F12', // 900
    //         dark: '#A16207', // 700
    //         base: '#EAB308', // 500
    //         light: '#FDE047', // 300
    //         extraLight: '#FEF9C3', // 100
    //         ultraLight: '#FEFCE8', // 50
    //     });
    // case ColorScheme.lime:
    //     return getAdjustedHexs({
    //         ultraDark: '#1A2E05', // 950
    //         extraDark: '#365314', // 900
    //         dark: '#4D7C0F', // 700
    //         base: '#84CC16', // 500
    //         light: '#BEF264', // 300
    //         extraLight: '#ECFCCB', // 100
    //         ultraLight: '#F7FEE7', // 50
    //     });
    // case ColorScheme.green:
    //     return getAdjustedHexs({
    //         ultraDark: '#052E16', // 950
    //         extraDark: '#14532D', // 900
    //         dark: '#15803D', // 700
    //         base: '#22C55E', // 500
    //         light: '#86EFAC', // 300
    //         extraLight: '#DCFCE7', // 100
    //         ultraLight: '#F0FDF4', // 50
    //     });
    // case ColorScheme.emerald:
    //     return getAdjustedHexs({
    //         ultraDark: '#022C22', // 950
    //         extraDark: '#064E3B', // 900
    //         dark: '#047857', // 700
    //         base: '#10B981', // 500
    //         light: '#6EE7B7', // 300
    //         extraLight: '#D1FAE5', // 100
    //         ultraLight: '#ECFDF5', // 50
    //     });
    // case ColorScheme.teal:
    //     return getAdjustedHexs({
    //         ultraDark: '#042F2E', // 950
    //         extraDark: '#134E4A', // 900
    //         dark: '#0F766E', // 700
    //         base: '#14B8A6', // 500
    //         light: '#5EEAD4', // 300
    //         extraLight: '#CCFBF1', // 100
    //         ultraLight: '#F0FDFA', // 50
    //     });
    // case ColorScheme.cyan:
    //     return getAdjustedHexs({
    //         ultraDark: '#083344', // 950
    //         extraDark: '#164E63', // 900
    //         dark: '#0E7490', // 700
    //         base: '#06B6D4', // 500
    //         light: '#67E8F9', // 300
    //         extraLight: '#CFFAFE', // 100
    //         ultraLight: '#ECFEFF', // 50
    //     });
    // case ColorScheme.sky:
    //     return getAdjustedHexs({
    //         ultraDark: '#082F49', // 950
    //         extraDark: '#0C4A6E', // 900
    //         dark: '#0369A1', // 700
    //         base: '#0EA5E9', // 500
    //         light: '#7DD3FC', // 300
    //         extraLight: '#E0F2FE', // 100
    //         ultraLight: '#F0F9FF', // 50
    //     });
    // case ColorScheme.blue:
    //     return getAdjustedHexs({
    //         ultraDark: '#172554', // 950
    //         extraDark: '#1E3A8A', // 900
    //         dark: '#1D4ED8', // 700
    //         base: '#3B82F6', // 500
    //         light: '#93C5FD', // 300
    //         extraLight: '#DBEAFE', // 100
    //         ultraLight: '#EFF6FF', // 50
    //     });
    // case ColorScheme.indigo:
    //     return getAdjustedHexs({
    //         ultraDark: '#1E1B4B', // 950
    //         extraDark: '#312E81', // 900
    //         dark: '#4338CA', // 700
    //         base: '#6366F1', // 500
    //         light: '#A5B4FC', // 300
    //         extraLight: '#E0E7FF', // 100
    //         ultraLight: '#EEF2FF', // 50
    //     });
    // case ColorScheme.violet:
    //     return getAdjustedHexs({
    //         ultraDark: '#2E1065', // 950
    //         extraDark: '#4C1D95', // 900
    //         dark: '#6D28D9', // 700
    //         base: '#8B5CF6', // 500
    //         light: '#C4B5FD', // 300
    //         extraLight: '#EDE9FE', // 100
    //         ultraLight: '#F5F3FF', // 50
    //     });
    // case ColorScheme.purple:
    //     return getAdjustedHexs({
    //         ultraDark: '#3B0764', // 950
    //         extraDark: '#581C87', // 900
    //         dark: '#7E22CE', // 700
    //         base: '#A855F7', // 500
    //         light: '#D8B4FE', // 300
    //         extraLight: '#F3E8FF', // 100
    //         ultraLight: '#FAF5FF', // 50
    //     });
    // case ColorScheme.fuchsia:
    //     return getAdjustedHexs({
    //         ultraDark: '#4A044E', // 950
    //         extraDark: '#701A75', // 900
    //         dark: '#A21CAF', // 700
    //         base: '#D946EF', // 500
    //         light: '#F0ABFC', // 300
    //         extraLight: '#FAE8FF', // 100
    //         ultraLight: '#FDF4FF', // 50
    //     });
    // case ColorScheme.pink:
    //     return getAdjustedHexs({
    //         ultraDark: '#500724', // 950
    //         extraDark: '#831843', // 900
    //         dark: '#BE185D', // 700
    //         base: '#EC4899', // 500
    //         light: '#F9A8D4', // 300
    //         extraLight: '#FCE7F3', // 100
    //         ultraLight: '#FDF2F8', // 50
    //     });
    // case ColorScheme.rose:
    // return getAdjustedHexs({
    //     ultraDark: '#4C0519', // 950
    //     extraDark: '#881337', // 900
    //     dark: '#BE123C', // 700
    //     base: '#F43F5E', // 500
    //     light: '#FDA4AF', // 300
    //     extraLight: '#FFE4E6', // 100
    //     ultraLight: '#FFF1F2', // 50
    // });
    default:
    case ColorScheme.default:
      return getAdjustedHexs({
        ultraDark: '#030712', // 950
        extraDark: '#111827', // 900
        dark: '#374151', // 700
        base: '#6B7280', // 500
        light: '#D1D5DB', // 300
        extraLight: '#F3F4F6', // 100
        ultraLight: '#F9FAFB', // 50
      });
  }
}

type ColorHexs = {
  extraIntense: string;
  intense: string;
  base: string;
  muted: string;
  extraMuted: string;
};

export function getColorFromSubTheme(subTheme?: string): Color | undefined {
  const keys = Object.keys(ColorScheme);
  const index = keys.findIndex((o) => o === subTheme);

  return index != null && index >= 3 ? index - 3 : undefined;
}

export function getBackgroundThemeRevisionFromTheme(backgroundTheme: BackgroundTheme): BackgroundThemeRevision {
  return {
    id: backgroundTheme.id,
    createdDateTime: backgroundTheme.createdDateTime,
    lastModifiedDateTime: backgroundTheme.lastModifiedDateTime,

    themeId: backgroundTheme.id,
    theme: backgroundTheme,
    scale: backgroundTheme.defaultScale,

    colorId1: backgroundTheme.defaultColorId1,
    colorId2: backgroundTheme.defaultColorId2,
    colorId3: backgroundTheme.defaultColorId3,

    intensity: backgroundTheme.defaultIntensity,
    opacityBlur: backgroundTheme.defaultOpacityBlur,
    subTheme: backgroundTheme.defaultSubTheme,

    media: backgroundTheme.defaultMedia,
    mediaRepeat: backgroundTheme.defaultMediaRepeat,
    mediaSize: backgroundTheme.defaultMediaSize,
    mediaPosition: backgroundTheme.defaultMediaPosition,
    mediaAttachment: backgroundTheme.defaultMediaAttachment,

    mediaMobile: backgroundTheme.defaultMediaMobile,
    mediaRepeatMobile: backgroundTheme.defaultMediaRepeatMobile,
    mediaSizeMobile: backgroundTheme.defaultMediaSizeMobile,
    mediaPositionMobile: backgroundTheme.defaultMediaPositionMobile,
    mediaAttachmentMobile: backgroundTheme.defaultMediaAttachmentMobile,
  };
}
export function getPageThemeRevisionFromTheme(pageTheme: PageTheme): PageThemeRevision {
  return {
    id: pageTheme.id,
    createdDateTime: pageTheme.createdDateTime,
    lastModifiedDateTime: pageTheme.lastModifiedDateTime,

    themeId: pageTheme.id,
    theme: pageTheme,

    font: pageTheme.defaultFont,
    primaryColorHex: pageTheme.defaultPrimaryColorHex,
    secondaryColorHex: pageTheme.defaultSecondaryColorHex,
    tertiaryColorHex: pageTheme.defaultTertiaryColorHex,

    pageSize: pageTheme.defaultPageSize,
    pageLayout: pageTheme.defaultPageLayout,
    lightMode: pageTheme.defaultLightMode,
  };
}
export function getGroupThemeRevisionFromTheme(groupTheme: GroupTheme): GroupThemeRevision {
  return {
    id: groupTheme.id,
    createdDateTime: groupTheme.createdDateTime,
    lastModifiedDateTime: groupTheme.lastModifiedDateTime,

    themeId: groupTheme.id,
    theme: groupTheme,

    backgroundColorId: groupTheme.defaultBackgroundColorId,
    borderColorId: groupTheme.defaultBorderColorId,
    titleColorId: groupTheme.defaultTitleColorId,
    titleSize: groupTheme.defaultTitleSize,
    titleWeight: groupTheme.defaultTitleWeight,
    divider: groupTheme.defaultDivider,
    border: groupTheme.defaultBorder,
    cardCornerStyle: groupTheme.defaultCardCornerStyle,
    initialDisplay: groupTheme.defaultInitialDisplay,

    intensity: groupTheme.defaultIntensity,
    opacity: groupTheme.defaultOpacity,
    opacityBlur: groupTheme.defaultOpacityBlur,

    subTheme: groupTheme.defaultSubTheme,

    media: groupTheme.defaultMedia,
    mediaRepeat: groupTheme.defaultMediaRepeat,
    mediaSize: groupTheme.defaultMediaSize,
    mediaPosition: groupTheme.defaultMediaPosition,
    mediaAttachment: groupTheme.defaultMediaAttachment,
  };
}
export function getReferralThemeRevisionFromTheme(referralTheme: ReferralTheme): ReferralThemeRevision {
  return {
    id: referralTheme.id,
    createdDateTime: referralTheme.createdDateTime,
    lastModifiedDateTime: referralTheme.lastModifiedDateTime,

    themeId: referralTheme.id,
    theme: referralTheme,

    backgroundColorId: referralTheme.defaultBackgroundColorId,
    borderColorId: referralTheme.defaultBorderColorId,
    textColorId: referralTheme.defaultTextColorId,

    opacity: referralTheme.defaultOpacity,
    opacityBlur: referralTheme.defaultOpacityBlur,

    cardCornerStyle: referralTheme.defaultCardCornerStyle,
    titleSize: referralTheme.defaultTitleSize,
    titleWeight: referralTheme.defaultTitleWeight,
    titleClamp: referralTheme.defaultTitleClamp,
    bodyClamp: referralTheme.defaultBodyClamp,
    expandableBody: referralTheme.defaultExpandableBody,
    border: referralTheme.defaultBorder,
    layout: referralTheme.defaultLayout,
    initialDisplay: referralTheme.defaultInitialDisplay,

    imageCornerStyle: referralTheme.defaultImageCornerStyle,
    imageRatio: referralTheme.defaultImageRatio,
    imageSize: referralTheme.defaultImageSize,

    subTheme: referralTheme.defaultSubTheme,
  };
}
export function getNavbarThemeRevisionFromTheme(navbarTheme: NavbarTheme): NavbarThemeRevision {
  return {
    id: navbarTheme.id,
    createdDateTime: navbarTheme.createdDateTime,
    lastModifiedDateTime: navbarTheme.lastModifiedDateTime,

    themeId: navbarTheme.id,
    theme: navbarTheme,

    navbarSize: navbarTheme.defaultNavbarSize,
    navbarStyle: navbarTheme.defaultNavbarStyle,

    logoStyle: navbarTheme.defaultLogoStyle,
    subTheme: navbarTheme.defaultSubTheme,
  };
}
export function getHeaderThemeRevisionFromTheme(headerTheme: HeaderTheme): HeaderThemeRevision {
  return {
    id: headerTheme.id,
    createdDateTime: headerTheme.createdDateTime,
    lastModifiedDateTime: headerTheme.lastModifiedDateTime,

    themeId: headerTheme.id,
    theme: headerTheme,

    backgroundColorId: headerTheme.defaultBackgroundColorId,
    borderColorId: headerTheme.defaultBorderColorId,
    textColorId: headerTheme.defaultTextColorId,

    cardCornerStyle: headerTheme.defaultCardCornerStyle,
    titleSize: headerTheme.defaultTitleSize,
    titleWeight: headerTheme.defaultTitleWeight,
    border: headerTheme.defaultBorder,
    padding: headerTheme.defaultPadding,
    layout: headerTheme.defaultLayout,

    imageSize: headerTheme.defaultImageSize,
    imageCornerStyle: headerTheme.defaultImageCornerStyle,

    subTheme: headerTheme.defaultSubTheme,
    opacity: headerTheme.defaultOpacity,
    opacityBlur: headerTheme.defaultOpacityBlur,

    socialSubTheme: headerTheme.defaultSocialSubTheme,
    socialPadding: headerTheme.defaultSocialPadding,
    socialCornerStyle: headerTheme.defaultSocialCornerStyle,
    socialLabel: headerTheme.defaultSocialLabel,
    socialLinkBackgroundColorId: headerTheme.defaultSocialLinkBackgroundColorId,
    socialLinkTextColorId: headerTheme.defaultSocialLinkTextColorId,
  };
}

export function themeValuesToStyle(
  imageSrc: string | undefined,
  mediaProperties: {
    mediaRepeat: MediaRepeat;
    mediaSize: MediaSize;
    mediaPosition: MediaPosition;
    opacity?: OpacityLevel;
  },
  cacheBuster?: string
): React.CSSProperties {
  const { mediaRepeat, mediaSize, mediaPosition, opacity } = mediaProperties;
  const repeatMap: Record<string, string> = {
    none: 'no-repeat',
    repeat: 'repeat',
    repeatX: 'repeat-x',
    repeatY: 'repeat-y',
  };
  const sizeMap: Record<string, string> = {
    auto: 'auto',
    cover: 'cover',
    contain: 'contain',
  };
  const positionMap: Record<string, string> = {
    center: 'center',
    left: 'left',
    right: 'right',
    top: 'top',
    bottom: 'bottom',
  };

  return imageSrc
    ? {
        backgroundImage: `url('${imageSrc}${imageSrc.startsWith('blob:') ? '' : `${imageSrc.includes('?') ? '&' : '?'}cachebuster=${cacheBuster}`}')`,
        backgroundRepeat: repeatMap[mediaRepeat],
        backgroundSize: sizeMap[mediaSize],
        backgroundPosition: positionMap[mediaPosition],
        opacity: getOpacityStyle(opacity),
      }
    : {};
}
